import { useLocation } from '@reach/router'
import { TFunctionResult } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'

import Box, { BoxProps } from '@/modules/components/Box'
import Flex from '@/modules/components/Flex'
import Icon from '@/modules/components/Icon'
import Typography from '@/modules/components/Typography'
import { LowerRegions } from '@/modules/configs/region'
import { getRegionPath } from '@/modules/utils'

type RegionMenuProps = {
  onClick: (region: string) => void
  suggestRegion: LowerRegions[]
  allRegion: LowerRegions[]
} & Omit<BoxProps, 'onClick'>

const RegionColLayout = {
  oneCol: '100%',
  twoCol: 'calc(100% / 2)',
} as const

export const RegionMenu = ({
  suggestRegion,
  allRegion,
  onClick,
  ...restProps
}: RegionMenuProps) => {
  const { t } = useTranslation('navigation')

  const location = useLocation()
  const regionPath = getRegionPath(location.pathname)

  const getRegionFlagIcon = (region: string) => {
    const flagStr = `${region.toUpperCase()}Flag`
    const RegionIcon = (Icon as any)[flagStr]
    return React.createElement(RegionIcon, {
      fontSize: '24px',
      border:
        regionPath === region || (region === 'global' && regionPath === '')
          ? '1.5px solid #1199FA'
          : '1.5px solid #000000',
      borderRadius: '50%',
      marginLeft: '16px',
    })
  }

  const renderRegionItem = (region: LowerRegions) => {
    return (
      <>
        {getRegionFlagIcon(region)}
        <Typography
          variant="subtitle3"
          color={
            regionPath === region || (region === 'global' && regionPath === '')
              ? 'primaryBrightBlue'
              : 'primaryDeepBlack'
          }
          maxWidth={['200px', 'max-content']}
          className="text"
          mx="6px"
        >
          {t(`geolocation.${region}`)}
        </Typography>
        <Typography
          variant="subtitle4"
          color={
            regionPath === region || (region === 'global' && regionPath === '')
              ? 'primaryBrightBlue'
              : '#656E85'
          }
        >
          {region.toUpperCase()}
        </Typography>
      </>
    )
  }

  const renderRegionList = (region: LowerRegions, width: string) => (
    <Flex
      key={region}
      onClick={() => {
        //  TODO improve lower case an upper case, cookie now is UPPERCASE❗️
        onClick(region.toUpperCase())
      }}
      justifyContent="flex-start"
      alignItems="center"
      width={width}
      borderRadius="8px"
      py={0.5}
      marginBottom={1}
      customCss={{
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: '#EAEEF4',
          '.text': {
            textDecoration: 'underline',
          },
        },
      }}
    >
      {renderRegionItem(region)}
    </Flex>
  )

  const renderSection = (title: TFunctionResult, regions: LowerRegions[]) =>
    regions && regions.length > 0 ? (
      <>
        <Typography
          variant="subtitle2"
          color="#000"
          marginBottom="16px"
          customCss={{
            '&:hover': {
              cursor: 'pointer',
            },
          }}
        >
          {title}
        </Typography>
        <Box display="flex" flexWrap="wrap" marginBottom={4}>
          {regions.map((region: LowerRegions) =>
            renderRegionList(region, RegionColLayout.twoCol)
          )}
        </Box>
      </>
    ) : null

  return (
    <Box {...restProps}>
      {renderSection(t('geolocation.suggestion'), suggestRegion as any)}
      {renderSection(t('geolocation.allLocation'), allRegion)}
    </Box>
  )
}
